import { applyCleValidationErrorsToFormik } from "@/api/cle-api/errors";
import Button from "@/components/bricks/button/Button";
import { CleApiErrorAlert } from "@/components/cle-api/CleApiErrorAlert";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import EditIcon from "@/components/icons/EditIcon";
import Modal, {
  ModalContext,
  ModalProvider,
  ModalTrigger,
} from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import type {
  TeachingEvidenceModel,
  UpdateTeachingRequest,
} from "@/api/cle-api";
import { useUpdateTeaching } from "@/hooks/cle-hooks";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext, useMemo } from "react";
import * as Yup from "yup";
import { YupNumberOfLessonsNullable } from "../../../util/yup";

export interface TeachingValidationFormValues {
  description: string;
  numberOfLessons: string;
}

const validationSchema = Yup.object({
  description: Yup.string().trim(),
  numberOfLessons: YupNumberOfLessonsNullable(),
});

export const teachingToValidationFormValues = (
  teaching: TeachingEvidenceModel,
): TeachingValidationFormValues => ({
  description: teaching.description ?? "",
  numberOfLessons:
    teaching.numberOfLessons != null ? String(teaching.numberOfLessons) : "",
});

export const validationFormValuesToUpdateRequest = (
  values: TeachingValidationFormValues,
  initialValues: TeachingValidationFormValues,
): UpdateTeachingRequest => {
  const body: UpdateTeachingRequest = {};

  const description = values.description.trim() || null;
  const initialDescription = initialValues.description.trim() || null;
  if (description !== initialDescription) {
    body.description = description;
  }

  const numberOfLessons =
    values.numberOfLessons === "" ? undefined : Number(values.numberOfLessons);
  const initialNumberOfLessons =
    initialValues.numberOfLessons === ""
      ? undefined
      : Number(initialValues.numberOfLessons);
  if (numberOfLessons !== initialNumberOfLessons) {
    body.number_of_lessons = numberOfLessons;
  }

  return body;
};

interface TeachingValidationEditFormProps {
  teaching: TeachingEvidenceModel;
  onSave: (updated: TeachingEvidenceModel) => void;
}

const TeachingValidationEditForm = ({
  teaching,
  onSave,
}: TeachingValidationEditFormProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const {
    mutate: updateTeaching,
    isPending,
    isError,
    error,
    reset: resetMutation,
  } = useUpdateTeaching();

  const initialFormValues = useMemo(
    () => teachingToValidationFormValues(teaching),
    [teaching],
  );

  const formik = useFormik<TeachingValidationFormValues>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const body = validationFormValuesToUpdateRequest(
        values,
        initialFormValues,
      );
      if (Object.keys(body).length === 0) return;

      updateTeaching(
        {
          teachingId: teaching.id,
          userId: teaching.ownerUserId,
          body,
        },
        {
          onSuccess: (updated) => {
            onSave(updated);
            resetMutation();
            setModalOpen(false);
          },
          onError: (err) => {
            applyCleValidationErrorsToFormik(err, formik.setFieldError);
          },
        },
      );
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    resetMutation();
    setModalOpen(false);
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-item-list c-modal-container-inner">
            <div className="flex-100">
              <label className="form-label" htmlFor="description">
                Beskrivelse
              </label>
              <textarea
                className="form-control"
                id="description"
                name="description"
                rows={4}
                value={formik.values.description}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="numberOfLessons">
                Antal lektioner
              </label>
              <input
                autoComplete="off"
                className={`form-control${
                  formik.touched.numberOfLessons &&
                  formik.errors.numberOfLessons
                    ? " is-invalid"
                    : ""
                }`}
                id="numberOfLessons"
                min="0"
                name="numberOfLessons"
                step="any"
                type="number"
                value={formik.values.numberOfLessons}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <ValidationErrorMessage
                touched={formik.touched.numberOfLessons}
                errorMessage={formik.errors.numberOfLessons}
              />
            </div>
          </div>
          {isError && (
            <div className="c-modal-container-inner">
              <CleApiErrorAlert
                error={error}
                fallback="Kunne ikke gemme opdatering."
              />
            </div>
          )}
          <StickyFooter className="border-top">
            <Button color="secondary" type="button" onClick={handleCancel}>
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button
                color="primary"
                disabled={isPending || !formik.dirty}
                type="submit"
              >
                Gem
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

interface TeachingValidationEditModalProps {
  teaching: TeachingEvidenceModel;
  onSave: (updated: TeachingEvidenceModel) => void;
}

export const TeachingValidationEditModal = ({
  teaching,
  onSave,
}: TeachingValidationEditModalProps) => (
  <ModalProvider>
    <ModalTrigger className="btn btn-primary btn--transparent has-icon px-2 edit-trigger">
      Redigér
      <EditIcon height={16} />
    </ModalTrigger>
    <Modal title="Redigér undervisning">
      <TeachingValidationEditForm teaching={teaching} onSave={onSave} />
    </Modal>
  </ModalProvider>
);
